import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import NotificationBell from '../components/NotificationBell';
import { SkeletonCard, SkeletonStats, SkeletonCalendar, SkeletonButton } from '../components/Skeletons';
import { Settings, TrendingUp, Zap, BookOpen, Trophy, Coins, ClipboardList, Target, Camera, Mic, FileText, WifiOff, Flame } from 'lucide-react';
import axios from 'axios';

export default function StudentHome() {
  const { user, profile, token, API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wotd, setWotd] = useState(null);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const res = await axios.get(`${API}/student/home`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setData(res.data);
        // Fetch Worksheet of the Day
        try {
          const wsRes = await axios.get(`${API}/student/worksheet-of-day`, {
            headers: { Authorization: `Bearer ${token}` }, withCredentials: true
          });
          setWotd(wsRes.data);
        } catch (e) { console.log('WOTD fetch error:', e); }
      } catch (err) {
        console.error('Home fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchHome();
  }, [API, token]);

  const streak = data?.streak || {};
  const todayDone = data?.today_completed;
  const last7 = data?.last_7_days || [];
  const sub = data?.subscription;
  const isFree = sub?.plan === 'free';
  const weekCount = data?.week_session_count || 0;
  const coins = data?.coins || { balance: 0 };
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen pb-safe" style={{ background: '#FFF8F0' }}>
      {/* Top Bar */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <div>
          <p className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>
            {loading ? 'Loading...' : `${getGreeting()}, ${user?.name?.split(' ')[0] || 'Student'}!`}
          </p>
          <p className="text-xs font-semibold" style={{ color: '#8B7355' }}>
            {loading ? '...' : `Class ${profile?.class_level || data?.profile?.class_level || '?'} · ${profile?.board || data?.profile?.board || 'CBSE'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* MathCoins */}
          <div className="bg-[#FFD23F]/20 border-2 border-[#FFD23F] rounded-full px-3 py-1.5 flex items-center gap-1" data-testid="coin-balance">
            <Coins size={14} color="#FFD23F" />
            <span className="text-xs font-black" style={{ color: '#1B4F8A' }}>{coins.balance || 0}</span>
          </div>
          <NotificationBell />
          <button data-testid="home-settings-btn" onClick={() => navigate('/profile')} className="w-10 h-10 rounded-full flex items-center justify-center bg-white border-2" style={{ borderColor: '#E5DACE' }}>
            <Settings size={18} color="#1B4F8A" />
          </button>
        </div>
      </div>

      <div className="px-4 space-y-4">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCalendar />
            <SkeletonButton />
            <SkeletonStats />
          </>
        ) : (
          <>
            {/* Streak Card */}
            <div className="bg-white border-2 rounded-3xl p-5 flex items-center gap-4" style={{ borderColor: '#E5DACE', boxShadow: '0 4px 0 #E5DACE' }} data-testid="streak-card">
              <div className="text-5xl animate-streak-fire">
                {streak.current_streak > 0 ? '🔥' : '💤'}
              </div>
              <div className="flex-1">
                <p className="text-3xl font-black" style={{ color: '#FF6B35' }}>{streak.current_streak || 0}</p>
                <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>
                  {streak.current_streak > 0 ? `Day ${streak.current_streak} streak! Keep it going!` : 'Start your streak today!'}
                </p>
              </div>
              <button onClick={() => navigate('/leaderboard')} data-testid="leaderboard-btn"
                className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#FFD23F20' }}>
                <Trophy size={18} color="#FFD23F" />
              </button>
            </div>

            {/* Weekly Calendar Strip */}
            <div className="flex justify-between gap-1" data-testid="weekly-calendar">
              {last7.map((day, i) => {
                const d = new Date(day.date + 'T00:00:00');
                const dayName = dayNames[d.getDay()];
                const isToday = day.date === new Date().toISOString().split('T')[0];
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-bold" style={{ color: isToday ? '#FF6B35' : '#8B7355' }}>{dayName}</span>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all"
                      style={{
                        background: day.practiced ? '#FF6B35' : 'white',
                        borderColor: day.practiced ? '#FF6B35' : isToday ? '#FFD23F' : '#E5DACE',
                        color: day.practiced ? 'white' : '#8B7355'
                      }}>
                      {day.practiced ? '✓' : d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Main CTA */}
            {todayDone ? (
              <div data-testid="practice-complete-card" className="rounded-3xl p-5 text-center border-2"
                style={{ background: '#06D6A0', borderColor: '#04A77C', boxShadow: '0 4px 0 #04A77C' }}>
                <p className="text-white font-extrabold text-lg">Today's Practice Complete ✓</p>
                <p className="text-white/80 text-sm font-semibold mt-1">Score: {data?.today_session?.score_percent}%</p>
              </div>
            ) : (
              <button data-testid="start-practice-btn" onClick={() => navigate('/practice')}
                className="btn-press w-full rounded-3xl p-5 text-center animate-pulse-glow"
                style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918' }}>
                <p className="text-white font-extrabold text-lg">Start Today's Practice</p>
                <p className="text-white/80 text-sm font-semibold mt-1">10 questions · ~5 mins</p>
              </button>
            )}

            {/* Free plan warning */}
            {isFree && (
              <div className="bg-[#FFD23F]/20 border-2 border-[#FFD23F] rounded-2xl p-3 text-center">
                <p className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Free Plan: {weekCount}/3 sessions this week</p>
              </div>
            )}

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3" data-testid="stats-row">
              <div className="bg-white border-2 rounded-2xl p-3 text-center" style={{ borderColor: '#E5DACE' }}>
                <p className="text-xl font-black" style={{ color: '#1B4F8A' }}>{data?.total_attempted || 0}</p>
                <p className="text-xs font-semibold" style={{ color: '#8B7355' }}>Questions</p>
              </div>
              <div onClick={() => navigate('/profile')} className="bg-white border-2 rounded-2xl p-3 text-center cursor-pointer hover:border-[#FF6B35] transition-colors" style={{ borderColor: '#E5DACE' }}>
                <p className="text-xl font-black" style={{ color: '#FF6B35' }}>{streak.current_streak || 0}<Flame size={16} className="inline ml-0.5 text-[#FF6B35]" style={{ animation: (streak.current_streak || 0) > 0 ? 'pulse 2s infinite' : 'none' }} /></p>
                <p className="text-xs font-semibold" style={{ color: '#8B7355' }}>Streak</p>
              </div>
              <div className="bg-white border-2 rounded-2xl p-3 text-center" style={{ borderColor: '#E5DACE' }}>
                <p className="text-xl font-black" style={{ color: '#06D6A0' }}>{data?.weekly_score || 0}%</p>
                <p className="text-xs font-semibold" style={{ color: '#8B7355' }}>Weekly</p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-3 gap-2" data-testid="quick-links">
              <button onClick={() => navigate('/practice')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <BookOpen size={18} color="#FF6B35" />
                <span className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Daily</span>
              </button>
              <button data-testid="deep-practice-link" onClick={() => navigate('/deep-practice')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <Trophy size={18} color="#8338EC" />
                <span className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Deep</span>
              </button>
              <button data-testid="weakness-link" onClick={() => navigate('/weakness')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#E63946', boxShadow: '0 3px 0 #C52F3C' }}>
                <Target size={18} color="#E63946" />
                <span className="text-xs font-bold" style={{ color: '#E63946' }}>Weak</span>
              </button>
              <button data-testid="assignments-link" onClick={() => navigate('/assignments')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <ClipboardList size={18} color="#E63946" />
                <span className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Tasks</span>
              </button>
              <button data-testid="camera-doubt-link" onClick={() => navigate('/camera-doubt')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#8338EC', boxShadow: '0 3px 0 #6B2DC0' }}>
                <Camera size={18} color="#8338EC" />
                <span className="text-xs font-bold" style={{ color: '#8338EC' }}>Snap</span>
              </button>
              <button onClick={() => navigate('/zeno')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-2" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <Zap size={18} color="#FFD23F" />
                <span className="text-xs font-bold" style={{ color: '#1B4F8A' }}>Zeno</span>
              </button>
            </div>

            {/* Worksheet of the Day */}
            {wotd && !wotd.worksheet?.completed && (
              <button data-testid="wotd-card" onClick={() => navigate('/worksheet-of-day')}
                className="btn-press w-full bg-gradient-to-r from-[#8338EC] to-[#3A86FF] rounded-3xl p-4 text-left border-2"
                style={{ borderColor: '#6B2DC0', boxShadow: '0 4px 0 #4A20A0' }}>
                <div className="flex items-center gap-3">
                  <FileText size={24} color="white" />
                  <div className="flex-1">
                    <p className="text-white font-extrabold text-sm">Your Daily Worksheet</p>
                    <p className="text-white/80 text-xs font-semibold mt-0.5">
                      {wotd.worksheet?.total_questions} personalized questions
                      {wotd.worksheet?.weak_chapters_targeted?.length > 0 && ` · Targeting: ${wotd.worksheet.weak_chapters_targeted.slice(0, 2).join(', ')}`}
                    </p>
                  </div>
                  <span className="text-white font-bold text-xs bg-white/20 px-2 py-1 rounded-full">START</span>
                </div>
              </button>
            )}
            {wotd?.worksheet?.completed && (
              <div data-testid="wotd-complete" className="bg-white border-2 rounded-3xl p-4 flex items-center gap-3" style={{ borderColor: '#06D6A0', boxShadow: '0 3px 0 #04A77C' }}>
                <FileText size={20} color="#06D6A0" />
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>Daily Worksheet Done!</p>
                  <p className="text-xs font-semibold" style={{ color: '#06D6A0' }}>Score: {wotd.worksheet.score}%</p>
                </div>
              </div>
            )}

            {/* Extra Links */}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => navigate('/progress')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-1.5" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <TrendingUp size={16} color="#06D6A0" />
                <span className="text-[10px] font-bold" style={{ color: '#1B4F8A' }}>Progress</span>
              </button>
              <button onClick={() => navigate('/leaderboard')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-1.5" style={{ borderColor: '#E5DACE', boxShadow: '0 3px 0 #E5DACE' }}>
                <Trophy size={16} color="#FFD23F" />
                <span className="text-[10px] font-bold" style={{ color: '#1B4F8A' }}>Leaderboard</span>
              </button>
              <button data-testid="offline-practice-link" onClick={() => navigate('/offline-practice')} className="btn-press bg-white border-2 rounded-2xl p-3 flex flex-col items-center gap-1.5" style={{ borderColor: '#8338EC', boxShadow: '0 3px 0 #6B2DC0' }}>
                <WifiOff size={16} color="#8338EC" />
                <span className="text-[10px] font-bold" style={{ color: '#8338EC' }}>Offline</span>
              </button>
            </div>
          </>
        )}
      </div>

      <BottomNav active="home" />
    </div>
  );
}
